import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { news } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function NewsDetailPage() {
  const { id } = useParams();
  const article = news.find((n) => n.id === id);

  useDocumentMeta(
    article ? `${article.title} - 影视资讯` : "影视资讯详情",
    article ? article.summary : "欧美三级影视资讯详情页，聚焦影视新闻与专题解读。",
    article ? `${article.title},${article.tags.join(",")},欧美影视资讯` : "影视资讯"
  );

  const related = useMemo(() => {
    if (!article) return [];
    const sameCat = news.filter((n) => n.id !== article.id && n.category === article.category);
    const others = news.filter((n) => n.id !== article.id && n.category !== article.category);
    return [...sameCat, ...others].slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="mx-auto flex max-w-[1240px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-secondary-500">
          <i className="ri-newspaper-line"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">未找到该资讯</h1>
        <p className="mt-2 text-sm text-secondary-500">该文章可能已被移除，请返回资讯列表浏览更多内容。</p>
        <Link
          to="/news"
          className="mt-6 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: article.category }]} />

      <article className="mt-5">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-700">
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-secondary-500">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <p className="mt-3 border-l-2 border-accent-400 pl-3 text-sm leading-relaxed text-secondary-600">
              {article.summary}
            </p>

            <div className="mt-6 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 影视资讯`}
                className="h-full w-full object-cover object-center"
              />
            </div>

            <div className="mt-6 space-y-4 text-sm leading-8 text-foreground-700 md:text-base md:leading-9">
              {article.paragraphs.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3 border-t border-background-200/80 pt-6">
              <Link
                to="/news"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯列表
              </Link>
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-grid-line"></i>
                浏览电影分类
              </Link>
            </div>
          </div>

          <aside>
            <div className="sticky top-24 rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-lg text-accent-600"></i>
                相关资讯推荐
              </h2>
              <div className="mt-4 space-y-4">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news-detail/${item.id}`}
                    className="group flex cursor-pointer gap-3"
                  >
                    <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 影视资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="line-clamp-2 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </h3>
                      <span className="mt-1 block text-xs text-secondary-500">{item.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-12 md:mt-16">
          <SectionHeading title="更多推荐" subtitle="继续阅读你可能感兴趣的内容" moreTo="/news" moreLabel="全部资讯" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/news-detail/${item.id}`}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 hover:shadow-md"
              >
                <div className="h-40 w-full overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - 影视资讯`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <span className="w-fit rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">
                    {item.category}
                  </span>
                  <h3 className="mt-2 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                    {item.title}
                  </h3>
                  <span className="mt-auto pt-3 text-xs text-secondary-500">{item.date}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}